"use client";

import { motion } from "motion/react";
import { useInView } from "react-intersection-observer";
import { FileText, Shield, Scale } from "lucide-react";
import { Logo } from "../Logo";
import { appLinks } from "../../routes";

export default function Footer() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const links = [
    {
      icon: <FileText className="w-4 h-4" />,
      label: "Mentions légales",
      path: appLinks.legalNotices,
    },
    {
      icon: <Shield className="w-4 h-4" />,
      label: "Politique de confidentialité",
      path: appLinks.privacyPolicy,
    },
    { icon: <Scale className="w-4 h-4" />, label: "CGU", path: appLinks.cgu },
  ];

  return (
    <footer
      ref={ref}
      className="bg-primary-light py-12 border-t border-stone-200 mt-12"
    >
      <div className="section-container">
        <div className="grid md:grid-cols-3 gap-12 mb-8">
          {/* Logo & Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Logo />
            <p className="text-gray-600 text-sm leading-relaxed">
              La plateforme qui redonne de la visibilité aux formateurs de
              qualité.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              Liens rapides
            </h4>
            <ul className="space-y-3">
              {links.map((link, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                >
                  <a
                    href={link.path}
                    target="_blank"
                    className="flex items-center gap-2 text-slate-600 hover:text-primary-600 transition-colors text-sm group"
                  >
                    <span className="text-slate-600 group-hover:text-primary-600 transition-colors">
                      {link.icon}
                    </span>
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="font-bold text-gray-900 mb-4">Contact</h4>
            <a
              href="mailto:contact@formly-app.fr"
              className="text-slate-600 hover:text-primary-600 transition-colors text-sm inline-flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              contact@formly-app.fr
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="h-px bg-gray-300 mb-6 origin-left"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center text-sm text-gray-600"
        >
          © 2025 Formly. Tous droits réservés.
        </motion.div>
      </div>
    </footer>
  );
}
